import { isNil } from 'lodash';
import numeral from 'numeral';
import moment from 'moment';
import { isNewOrReopenIClassFund } from '../../funds/shared/shared-fund-notes-utility';
import { getFundMinimum, isNewOrReopen } from '../../funds/funds-utility';
import { isIRA } from '../../IRA/IRA-utility';
import {
  recurringSweepMin,
  UNIT,
  ROTH_CONVERSION_DEFAULT_MIN,
  ROTH_CONVERSION_DEFAULT_MAX,
  BENEFICIARY_TYPES,
  NEW_ICLASS_FUND_MIN_AMOUNT,
  EXISTING_ICLASS_FUND_MIN_AMOUNT,
  ROTH_CONVERSION_NEW_DEFAULT_MIN,
} from '../../../settings/settings';
import { isAutomaticSell, isAppSell } from 'src/utils/transactions-util';
import {
  coalesce,
  currencyNoPrecision,
  hasValue,
  truncateToTwoDecimalFormatted,
} from '../../utils';
import { isAPP, isRAS, getRasDailyMax } from '../../advice/advice-utility';

numeral.defaultFormat('$0,0.00');

export function validateExactLength(value, requiredLength, touched, message) {
  return touched &&
    typeof value === 'string' &&
    value.length !== 0 &&
    value.length !== requiredLength
    ? coalesce(message, `${requiredLength} characters required`)
    : '';
}

export function validateExactValue(
  value,
  requiredValue,
  touched,
  message = 'Invalid entry'
) {
  return touched && value !== requiredValue ? message : '';
}

export function validateRecurringBuyFund({
  fund,
  nonRetirementMaxLimit,
  overNonRetirementMaxLimit,
  shoppingCartTotalForSelectedIRA,
}) {
  delete fund.combinedError;
  delete fund.currentYearError;
  delete fund.daySelectionError;
  delete fund.monthSelectionError;
  const {
    currentYearAmount = 0,
    currentYearInclusiveIRAData,
    currentYearRemainingContributions,
    selected,
    transferDays = [],
    transferMonths = [],
  } = fund;

  const isSelectedWithCurrent =
    selected &&
    (currentYearAmount > 0 ||
      transferMonths.length > 0 ||
      transferDays.length > 0);

  if (!isSelectedWithCurrent) {
    return fund;
  }

  if (!isIRA(fund)) {
    const minValue = getFundMinimum(fund, 'currentYearLimit');
    if (isNaN(currentYearAmount)) {
      fund.currentYearError = `Please enter a valid numeric value`;
    } else if (currentYearAmount < minValue) {
      fund.currentYearError = `Minimum Amount: ${truncateToTwoDecimalFormatted(
        minValue
      )}`;
    } else if (currentYearAmount < 0) {
      fund.currentYearError = 'Amount has to be greater than 0.';
    } else if (overNonRetirementMaxLimit) {
      fund.currentYearError = `Daily Max: ${truncateToTwoDecimalFormatted(
        nonRetirementMaxLimit
      )}`;
    }
  } else {
    const newOrReopenIraMinimumFormatted = truncateToTwoDecimalFormatted(
      getFundMinimum(fund, 'currentYearLimit')
    ); // new or reopen should be handled in get fund min
    const currentYearMinimumLimit = getFundMinimum(fund, 'currentYearLimit');
    const currentYearMinimumFormatted = truncateToTwoDecimalFormatted(
      currentYearMinimumLimit
    );

    const canContributeCurrentYear = currentYearRemainingContributions !== 0;

    // validation for IRA funds
    const validateAmount = (
      amount,
      minLimit,
      remainingContributions,
      errorField,
      formattedAmount
    ) => {
      if (isNaN(amount)) {
        fund[errorField] = 'Please enter a valid numeric value';
      } else if (fund.sweep && amount < recurringSweepMin) {
        fund[errorField] = 'Min. Contribution: $100';
      } else if (
        fund.currentYearMaximumLimit < amount ||
        shoppingCartTotalForSelectedIRA > fund.currentYearMaximumLimit
      ) {
        fund[errorField] = 'Exceeds IRA annual contribution limit';
      } else if (overNonRetirementMaxLimit) {
        if (isNewOrReopen(fund) && canContributeCurrentYear) {
          fund.combinedDailyMaxError = `Daily Max: ${truncateToTwoDecimalFormatted(
            nonRetirementMaxLimit
          )}`;
        } else {
          fund[errorField] = `Daily Max: ${truncateToTwoDecimalFormatted(
            nonRetirementMaxLimit
          )}`;
        }
      } else if (isNewOrReopen(fund)) {
        // validation for combined Inputs when user can contribute to both Years and when the entered amount is negative
        if (
          currentYearAmount < numeral(newOrReopenIraMinimumFormatted).value()
        ) {
          // validation for single Inputs when user cannot contribute to both Years at the same time
          fund[
            errorField
          ] = `Min. Contribution: ${newOrReopenIraMinimumFormatted}`;
        }
      } else if (amount < minLimit) {
        fund[errorField] = `Min. Contribution: ${currencyNoPrecision(
          formattedAmount
        )}`;
      }
    };

    validateAmount(
      currentYearAmount,
      currentYearMinimumLimit,
      currentYearInclusiveIRAData,
      'currentYearError',
      currentYearMinimumFormatted
    );
  }
  const validateDaySelect = (errorField) => {
    if (!transferDays) {
      fund[errorField] = 'Please Select a Day';
    } else if (transferDays.length === 0) {
      fund[errorField] = 'Min. Day Select: 1';
    }
  };

  const validateMonthSelect = (errorField) => {
    if (!transferMonths) {
      fund[errorField] = 'Please Select a Month';
    } else if (transferMonths.length === 0) {
      fund[errorField] = 'Min. Month Select: 1';
    }
  };
  validateDaySelect('daySelectionError');
  validateMonthSelect('monthSelectionError');
  return fund;
}

export function validateBuyFund({
  fund,
  nonRetirementMaxLimit,
  overNonRetirementMaxLimit,
}) {
  delete fund.combinedError;
  delete fund.currentYearError;
  delete fund.priorYearError;
  const {
    currentYearAmount = 0,
    currentYearInclusiveIRAData,
    currentYearRemainingContributions,
    priorYearAmount = 0,
    priorYearInclusiveIRAData,
    priorYearRemainingContributions,
    selected,
    withinIRAPriorYearContributionPeriod,
  } = fund;
  const isSelectedWithCurrent = selected && currentYearAmount > 0;
  const isSelectedWithPrior = selected && priorYearAmount > 0;

  if (!isSelectedWithCurrent && !isSelectedWithPrior) {
    return fund;
  }
  if (!isIRA(fund)) {
    // will need to hard code minimum limit for newly added iclass fund
    const minValue = isNewOrReopenIClassFund(fund)
      ? NEW_ICLASS_FUND_MIN_AMOUNT
      : fund.fundType === 'ICLASS'
      ? EXISTING_ICLASS_FUND_MIN_AMOUNT
      : getFundMinimum(fund, 'currentYearLimit');
    if (isNaN(currentYearAmount)) {
      fund.currentYearError = `Please enter a valid numeric value`;
    } else if (currentYearAmount < minValue) {
      fund.currentYearError = `Minimum Amount: ${truncateToTwoDecimalFormatted(
        minValue
      )}`;
    } else if (currentYearAmount < 0) {
      fund.currentYearError = 'Amount has to be greater than 0.';
    } else if (overNonRetirementMaxLimit) {
      fund.currentYearError = `Daily Max: ${truncateToTwoDecimalFormatted(
        nonRetirementMaxLimit
      )}`;
    }
  } else {
    const newOrReopenIraMinimumFormatted = truncateToTwoDecimalFormatted(
      getFundMinimum(fund, 'currentYearLimit')
    ); // new or reopen should be handled in get fund min
    const priorYearMinimumLimit = getFundMinimum(fund, 'priorYearLimit');
    const currentYearMinimumLimit = getFundMinimum(fund, 'currentYearLimit');
    const currentYearMinimumFormatted = truncateToTwoDecimalFormatted(
      currentYearMinimumLimit
    );
    const priorYearMinimumFormatted = truncateToTwoDecimalFormatted(
      priorYearMinimumLimit
    );

    const canContributeCurrentYear = currentYearRemainingContributions !== 0;

    const canContributePriorYear = priorYearRemainingContributions > 0;
    // validation for IRA funds
    const validateAmount = (
      amount,
      minLimit,
      remainingContributions,
      errorField,
      formattedAmount
    ) => {
      if (isNaN(amount)) {
        fund[errorField] = 'Please enter a valid numeric value';
      } else if (fund.sweep && amount < 0.01) {
        fund[errorField] = 'Min. Contribution: $0.01';
      } else if (remainingContributions < 0) {
        fund[errorField] = 'Exceeds IRA annual contribution limit';
      } else if (overNonRetirementMaxLimit) {
        if (
          isNewOrReopen(fund) &&
          canContributeCurrentYear &&
          canContributePriorYear
        ) {
          fund.combinedDailyMaxError = `Daily Max: ${truncateToTwoDecimalFormatted(
            nonRetirementMaxLimit
          )}`;
        } else {
          fund[errorField] = `Daily Max: ${truncateToTwoDecimalFormatted(
            nonRetirementMaxLimit
          )}`;
        }
      } else if (isNewOrReopen(fund)) {
        // validation for combined Inputs when user can contribute to both Years and when the entered amount is negative
        if (withinIRAPriorYearContributionPeriod) {
          if (canContributeCurrentYear && canContributePriorYear) {
            if (
              priorYearAmount + currentYearAmount <
                numeral(newOrReopenIraMinimumFormatted).value() ||
              priorYearAmount < 0 ||
              currentYearAmount < 0
            ) {
              fund.combinedError = `Minimum required combined contribution ${
                fund.newFund ? 'on new' : 'to reopen'
              } IRAs is ${newOrReopenIraMinimumFormatted}`;
            }
          } else {
            // validation for single Inputs when user cannot contribute to both Years at the same time
            if (amount < minLimit) {
              fund[errorField] = `Min. Contribution: ${currencyNoPrecision(
                formattedAmount
              )}`;
            }
          }
        } else if (
          currentYearAmount < numeral(newOrReopenIraMinimumFormatted).value()
        ) {
          // validation for single Inputs when user cannot contribute to both Years at the same time
          fund[
            errorField
          ] = `Min. Contribution: ${newOrReopenIraMinimumFormatted}`;
        }
      } else if (amount < minLimit) {
        fund[errorField] = `Min. Contribution: ${currencyNoPrecision(
          formattedAmount
        )}`;
      }
    };

    if (isSelectedWithPrior) {
      validateAmount(
        priorYearAmount,
        priorYearMinimumLimit,
        priorYearInclusiveIRAData,
        'priorYearError',
        priorYearMinimumFormatted
      );
    }
    if (isSelectedWithCurrent) {
      validateAmount(
        currentYearAmount,
        currentYearMinimumLimit,
        currentYearInclusiveIRAData,
        'currentYearError',
        currentYearMinimumFormatted
      );
    }
  }

  return fund;
}

/**
 * Applies sell validation and clears or populates sell related errors: grossAmountError,
 *
 * @param {object} args
 * @param {SellFund} args.fund
 */
export function validateSellFund({
  fund,
  sellPendingTransactions,
  sellPendingTransactionsTotal,
  sellTransactionTotal,
  transactionLimit,
}) {
  const {
    availableShares,
    closeout,
    equityAsOf,
    fundLimitMessage,
    grossAmount,
    grossAmountDollars,
    grossUnit,
    hasPendingTransaction,
    hasUncollectedShares,
    redeemableFundDollars,
    redeemableFundPercent,
    redeemableFundShares,
    redeemMaxMessage,
    selected,
    shares,
  } = fund || {};

  if (!selected) {
    return;
  }
  delete fund.exceedsCloseoutDailyLimit;
  delete fund.exceededDailyLimit;
  delete fund.grossAmountError;
  const currAvailableShares = !isAutomaticSell() ? availableShares : shares;
  // Exit validation if we are not performing a closeout,
  // this was added to trigger validation, and set 'exceedsCloseoutDailyLimit' on the fund object
  // since the selector that calls validateSellFund uses previously cached value if there's no change to the fund object.
  if (
    !closeout &&
    (grossAmount === 0 || isNil(grossAmount) || isNil(transactionLimit))
  ) {
    return;
  }
  const sellDailyMax = transactionLimit.maximum;
  const exceedsRASWithdrawDailyMax =
    isRAS(fund.productType) &&
    grossAmountDollars > getRasDailyMax(fund, sellPendingTransactions) &&
    isAppSell();
  const fundsAmountTotalWithPendingTxnsTotal =
    Math.floor((sellTransactionTotal + sellPendingTransactionsTotal) * 100) /
    100;
  const meetsCloseoutCriteria = closeout && !hasUncollectedShares;
  const belowTransactionMinCriteria =
    isAutomaticSell() && fund.fundType === 'SUMMIT'
      ? grossAmountDollars < 100
      : grossAmountDollars < transactionLimit.minimum;
  if (closeout) {
    // Sum up all current transaction totals, pending transactions, and fund equityAsOf
    let totalIncludingSelectedFundAndPendingTxns =
      fundsAmountTotalWithPendingTxnsTotal;

    // Deduct estimated fund grossAmount if an input value exists for fund close out scenario,
    // since this value is to be disregarded for fund close outs, but has already been added to sellTransactionTotal from selector
    if (grossAmount && grossAmount > 0) {
      totalIncludingSelectedFundAndPendingTxns -= grossAmountDollars;
    }

    if (
      !isAutomaticSell() &&
      (equityAsOf > sellDailyMax ||
        totalIncludingSelectedFundAndPendingTxns > sellDailyMax)
    ) {
      fund.exceedsCloseoutDailyLimit = true;
    }
  }

  if (isNaN(grossAmount)) {
    fund.grossAmountError = fundLimitMessage;
    return;
  }

  const percentTotal = grossAmountDollars / equityAsOf;
  const exceedsAPPWithdrawThresholdCriteria =
    isAPP(fund.productType) &&
    (percentTotal > 0.85 || equityAsOf - grossAmountDollars < 10000) &&
    isAppSell();

  switch (grossUnit) {
    case UNIT.DOLLARS:
      // Show fund threshold max message as long as
      // grossAmountDollars doesn't exceed sellDailyMax and redeemableFundDollars
      if (
        grossAmountDollars <= sellDailyMax &&
        grossAmountDollars > redeemableFundDollars &&
        !isAutomaticSell()
      ) {
        fund.grossAmountError = redeemMaxMessage;
      } else if (belowTransactionMinCriteria) {
        fund.grossAmountError = fundLimitMessage;
      } else if (
        (fundsAmountTotalWithPendingTxnsTotal > sellDailyMax &&
          !isAutomaticSell() &&
          !isRAS(fund.productType)) ||
        (isAutomaticSell() && grossAmountDollars > sellDailyMax) ||
        (grossAmountDollars <= sellDailyMax &&
          exceedsAPPWithdrawThresholdCriteria) ||
        exceedsRASWithdrawDailyMax
      ) {
        fund.grossAmountError =
          isRAS(fund.productType) && isAppSell() ? '' : fundLimitMessage;
        fund.exceededDailyLimit = true;
      }
      break;
    case UNIT.PERCENT:
      // Show fund threshold max message as long as grossAmountDollars doesn't
      // exceed sellDailyMax, and grossAmountDollars is greater than redeemableFundDollars
      if (isAutomaticSell() && (grossAmount > 99 || grossAmount < 0)) {
        fund.grossAmountError = (
          <span>Please enter a percent that is between 0 and 99 percent.</span>
        );
      } else if (
        grossAmount > 100 ||
        (grossAmountDollars <= sellDailyMax &&
          hasPendingTransaction &&
          grossAmount > redeemableFundPercent &&
          !closeout &&
          !isAutomaticSell())
      ) {
        fund.grossAmountError = redeemMaxMessage;
      } else if (
        (fundsAmountTotalWithPendingTxnsTotal > sellDailyMax &&
          !isAutomaticSell()) ||
        (isAutomaticSell() && grossAmountDollars > sellDailyMax)
      ) {
        fund.grossAmountError = fundLimitMessage;
        fund.exceededDailyLimit = true;
      } else if (
        (grossAmount < 0.01 || belowTransactionMinCriteria) &&
        !meetsCloseoutCriteria
      ) {
        fund.grossAmountError = fundLimitMessage;
      }
      break;
    case UNIT.SHARES:
      // Show fund threshold max message as long as grossAmountDollars doesn't
      // exceed sellDailyMax, and grossAmountDollars is greater than redeemableFundDollars
      if (
        (grossAmountDollars <= sellDailyMax &&
          hasPendingTransaction &&
          !isAutomaticSell() &&
          grossAmount > redeemableFundShares) ||
        (grossAmount > currAvailableShares && !isAutomaticSell())
      ) {
        fund.grossAmountError = redeemMaxMessage;
      } else if (
        (grossAmount < 0.001 || belowTransactionMinCriteria) &&
        !meetsCloseoutCriteria
      ) {
        fund.grossAmountError = fundLimitMessage;
      } else if (
        (fundsAmountTotalWithPendingTxnsTotal > sellDailyMax &&
          !isAutomaticSell()) ||
        (isAutomaticSell() && grossAmountDollars > sellDailyMax)
      ) {
        fund.grossAmountError = fundLimitMessage;
        fund.exceededDailyLimit = true;
      }
      break;
    default:
      // should never happen, but just in case...
      if (isAppSell()) {
        fund.grossAmountError = 'Select $ or %';
      } else {
        fund.grossAmountError = 'Select $, %, or shares';
      }
      break;
  }
}

export function validateMaxLength(value, maxLength, touched, message) {
  return touched && typeof value === 'string' && value.length > maxLength
    ? coalesce(message, `${maxLength} characters max`)
    : '';
}

export function validateRequired(value, touched, message = 'Required') {
  return touched && !hasValue(value) ? message : '';
}

export function getRothConversionMinimum(fund, existingRothIRAFunds = []) {
  const isNewFund = !existingRothIRAFunds.some((f) => f.ticker === fund.ticker);
  return isNewFund
    ? ROTH_CONVERSION_NEW_DEFAULT_MIN
    : ROTH_CONVERSION_DEFAULT_MIN;
}

/**
 * TODO:
 * Validate Roth fund conversion input
 * @param {RothFund} fund
 * @param {RothFund[]} existingRothIraFunds
 * @returns {boolean}
 */
export const validateRothConversionFund = (fund, existingRothIraFunds = []) => {
  const {
    grossAmount,
    grossUnit,
    redeemableFundDollars,
    redeemableThresholdDollarPercentage,
    grossAmountDollars,
  } = fund;
  if (grossAmount === 0) {
    return true;
  }
  const ROTH_CONVERSION_MINIMUM = getRothConversionMinimum(
    fund,
    existingRothIraFunds
  );
  switch (grossUnit) {
    case UNIT.DOLLARS:
      return (
        grossAmount >= ROTH_CONVERSION_MINIMUM &&
        grossAmount <= ROTH_CONVERSION_DEFAULT_MAX &&
        grossAmount <= redeemableFundDollars
      );
    case UNIT.PERCENT:
      return (
        grossAmount <= 100 &&
        grossAmountDollars >= ROTH_CONVERSION_MINIMUM &&
        grossAmountDollars <= ROTH_CONVERSION_DEFAULT_MAX &&
        grossAmount <= redeemableThresholdDollarPercentage
      );
    case UNIT.SHARES:
      return (
        grossAmount <= fund.shares &&
        grossAmountDollars >= ROTH_CONVERSION_MINIMUM &&
        grossAmountDollars <= ROTH_CONVERSION_DEFAULT_MAX &&
        grossAmountDollars <= redeemableFundDollars
      );
    case UNIT.ALL_SHARES:
      return (
        grossAmountDollars >= ROTH_CONVERSION_MINIMUM &&
        grossAmountDollars <= ROTH_CONVERSION_DEFAULT_MAX
      );
    default:
      return true;
  }
};

/**
 * Validation for step 2 roth conversion fund amount input
 * @param {Fund} fund
 * @param {RothFund[]} existingRothIraFunds
 * @returns {boolean}
 */
export const validateRothConversionIRAFund = (
  fund,
  existingRothIraFunds = []
) => {
  const { grossAmountDollars } = fund;
  const ROTH_CONVERSION_MINIMUM = getRothConversionMinimum(
    fund,
    existingRothIraFunds
  );

  // check if meets within min max restrictions
  return (
    grossAmountDollars >= ROTH_CONVERSION_MINIMUM &&
    grossAmountDollars <= ROTH_CONVERSION_DEFAULT_MAX
  );
};

/**
 *
 * @param {Beneficiary} beneficiary
 * @returns { isValid: <boolean>; errors: Record<string, boolean> } }
 */
export const validateRothBeneficiary = (beneficiary) => {
  const checkProps =
    beneficiary.entityType === BENEFICIARY_TYPES.INDIVIDUAL
      ? ['firstName', 'lastName', 'relationship', 'distribution', 'dateOfBirth']
      : ['name', 'relationship', 'distribution'];

  const initialValidation = {
    isValid: true,
    errors: {},
  };

  // will return back beneficiary object with validation props
  return checkProps.reduce((bene, prop) => {
    let dateCheck = true;
    if (prop === 'dateOfBirth') {
      dateCheck =
        !beneficiary[prop] ||
        (beneficiary[prop] &&
          moment(beneficiary[prop], 'MM/DD/YYYY', true).isValid() &&
          moment(beneficiary[prop]).isBefore(new Date()));
    }

    if (!dateCheck || (prop !== 'dateOfBirth' && !beneficiary[prop])) {
      bene.isValid = false;
      bene.errors[prop] = true;
    }

    return bene;
  }, initialValidation);
};
